import { CheckSharp } from '@mui/icons-material';
import { MouseEventHandler, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IConvertPopoverProps } from '@work4all/components/lib/components/convert-popover/ConvertPopover';
import {
  InfoCard,
  InfoCards,
  PreviewTitle,
  PreviewWrapper,
} from '@work4all/components/lib/components/entity-preview/components';
import { PreviewInputsProvider } from '@work4all/components/lib/components/entity-preview/components/preview-input';
import { useEntityPreview } from '@work4all/components/lib/components/entity-preview/hooks/use-entity-preview';

import { useDataMutation, useInaccessibleFields } from '@work4all/data';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { shouldShowLookupField } from '@work4all/models/lib/lookup-field-definitions';

import { lookUpPreviewFields } from './LookUpPreviewContainer';

export type ILookUpPreviewProps = {
  lookUps: LookUp[];
  onCloseClick?: MouseEventHandler<HTMLButtonElement>;
  onEditClicked?: MouseEventHandler<HTMLButtonElement>;
  onShareClicked?: () => void;
  convertProps?: Pick<IConvertPopoverProps, 'exclude' | 'onClick'>;
  onVisibilityToggle?: (visible: boolean) => void;
};

export function LookUpPreview(props: ILookUpPreviewProps) {
  const {
    lookUps,
    onCloseClick,
    onEditClicked,
    onShareClicked,
    convertProps,
    onVisibilityToggle,
  } = props;

  const { t } = useTranslation();
  const { isInaccessible } = useInaccessibleFields();

  const nameIsAccessible = !isInaccessible(Entities.lookUp, 'name');

  const [mutate] = useDataMutation<LookUp, EMode.upsert>({
    entity: Entities.lookUp,
    mutationType: EMode.upsert,
    responseData: lookUpPreviewFields as unknown as LookUp,
  });

  const entityPreview = useEntityPreview({
    subEntityType: Entities.lookUp,
    entries: lookUps,
    mutate,
  });

  const titleRef = useRef<PreviewTitle>(null);

  const lookUp = lookUps[0];
  const canEdit = true;

  return (
    <PreviewInputsProvider
      {...entityPreview}
      entity={Entities.lookUp}
      entries={lookUps}
    >
      <PreviewWrapper>
        <PreviewTitle
          disabled={true}
          canEdit={canEdit}
          ref={titleRef}
          label={lookUp?.name}
          onCloseClick={onCloseClick}
          onEditClicked={onEditClicked}
          onShareClicked={onShareClicked}
          convertProps={convertProps}
          onVisibilityToggle={onVisibilityToggle}
        >
          {lookUp?.name}
        </PreviewTitle>

        <InfoCards columns={2}>
          {nameIsAccessible && (
            <InfoCard staticField label={t('COMMON.NAME')}>
              {lookUp?.name}
            </InfoCard>
          )}

          {lookUp?.groupView !== null &&
            shouldShowLookupField(lookUp?.lookupType, 'groupView') && (
              <InfoCard staticField label={t('COMMON.PROJECTKIND.GROUPVIEW')}>
                {Boolean(lookUp?.groupView) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CheckSharp />
                  </div>
                )}
              </InfoCard>
            )}

          {lookUp?.textValue !== null &&
            shouldShowLookupField(lookUp?.lookupType, 'textValue') && (
              <InfoCard staticField label={t('COMMON.GROUPING')}>
                {lookUp?.textValue}
              </InfoCard>
            )}

          {lookUp?.hexColorPair !== null &&
            shouldShowLookupField(lookUp?.lookupType, 'hexColorPair') && (
              <InfoCard staticField label={t('COMMON.COLOR')}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: lookUp.hexColorPair?.split(',')[0],
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                    }}
                  />
                  {lookUp.hexColorPair?.split(',')[0]}
                </div>
              </InfoCard>
            )}

          {lookUp?.standard !== null &&
            shouldShowLookupField(lookUp?.lookupType, 'standard') && (
              <InfoCard staticField label={t('COMMON.DEFAULT')}>
                {Boolean(lookUp?.standard) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CheckSharp />
                  </div>
                )}
              </InfoCard>
            )}

          {lookUp?.closedStatus !== null &&
            shouldShowLookupField(lookUp?.lookupType, 'closedStatus') && (
              <InfoCard staticField label={t('COMMON.CLOSED')}>
                {Boolean(lookUp?.closedStatus) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CheckSharp />
                  </div>
                )}
              </InfoCard>
            )}
        </InfoCards>
      </PreviewWrapper>
    </PreviewInputsProvider>
  );
}
